import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import Dialog from '@mui/material/Dialog';
import { DialogActions, DialogContent, DialogTitle } from '@mui/material';
import {
  activateRange,
  getPipelineModels,
  getTestTypes,
} from '../../api/manufactured_chips/manufacturedChipsApi';
import { formatActivateRangeParams } from '../../utilities/dataTransform';
import { camelCaseToTitleCase } from '../../utilities/utils';
import { HeaderTableCell } from '../../components/Table/HeaderTableCell';
import { BodyTableCell } from '../../components/Table/BodyTableCell';
import Colors from '../../theme/colors';
import {
  addNewBatchModalRules,
  BatchEntryStep,
  defaultFieldValues,
  ManufacturingErrorContent,
} from '.';
import ValidationTextField from '../../components/ValidationTextField/ValidationTextField';

import {
  ContentContainer,
  TitleContainer,
  Title,
  ActionButton,
  LabelsContainer,
  LabelsInputContainer,
  HalfScreen,
  Row,
  PageContainer,
  SectionTitle,
  HeaderTableRow,
} from './styles';
import { ModalDropdownInput } from '../../components/Modal';
import { formatDate } from '../../utilities/dateTimeUtils';
import DatePickerInput from '../../components/DatePickerInput/DatePickerInput';
import { Toast } from '../../components/Toast';
import useToast from '../../hooks/useToast';
import { ActivateRangeErrorCodes, PipelineModel } from '../../api/manufactured_chips';

function ManufacturingPage() {
  const [batchEntryStep, setBatchEntryStep] = React.useState<BatchEntryStep>(
    BatchEntryStep.DataEntry
  );

  const [pipelineModels, setPipelineModels] = React.useState<Array<PipelineModel>>([]);
  const [products, setProducts] = React.useState<Array<string>>([]);
  const [errorDialogContent, setErrorDialogContent] = React.useState<{
    title: string;
    description: string;
  }>();

  const [creatingBatch, setCreatingBatch] = React.useState(false);

  const { toastConfig, closeToast, setToastConfig } = useToast();

  const { handleSubmit, control, reset, watch, getValues } = useForm({
    defaultValues: defaultFieldValues,
  });

  const onSubmit = () => {
    setBatchEntryStep(BatchEntryStep.Summary);
  };

  const onEdit = () => {
    setBatchEntryStep(BatchEntryStep.DataEntry);
  };

  useEffect(() => {
    getPipelineModels().then((response) => {
      setPipelineModels(response.data);
    });
    getTestTypes().then((response) => {
      setProducts(response.data);
    });
  }, []);

  const onActivateRange = () => {
    setCreatingBatch(true);
    activateRange(formatActivateRangeParams(getValues()))
      .then(() => {
        setToastConfig({
          variant: 'success',
          title: 'Batch added successfully.',
          isOpen: true,
        });
        reset();
        setBatchEntryStep(BatchEntryStep.DataEntry);
      })
      .catch((error) => {
        const errorCode = error?.data?.error_code;
        const errorDetails = ManufacturingErrorContent.get(
          errorCode as ActivateRangeErrorCodes
        ) || {
          title: 'Error',
          description: 'An unexpected error occurred.',
        };
        setErrorDialogContent(errorDetails);
      })
      .finally(() => {
        setCreatingBatch(false);
      });
  };

  const renderDateInput = (props: any) => {
    return <DatePickerInput {...props} />;
  };

  const closeErrorDialog = () => {
    setBatchEntryStep(BatchEntryStep.DataEntry);
    setErrorDialogContent(undefined);
  };

  return (
    <PageContainer>
      {batchEntryStep === BatchEntryStep.DataEntry ? (
        <ContentContainer>
          <TitleContainer>
            <Title variant="Heading3" color={Colors.Text.Heading}>
              Enter New Batch
            </Title>
            <Typography variant="BodyMediumRegular" color={Colors.Text.Paragraph}>
              Fill in the details to register a new product batch.
            </Typography>
          </TitleContainer>
          <Row addBottomMargin>
            <HalfScreen isLeftHalf>
              <SectionTitle variant="BodyMediumBold" color={Colors.Text.Paragraph}>
                First Labels
              </SectionTitle>
              <LabelsContainer>
                <LabelsInputContainer>
                  <ValidationTextField
                    control={control}
                    rules={addNewBatchModalRules.firstLabel}
                    name="firstLabel"
                    label="Scan or enter first label"
                  />
                </LabelsInputContainer>
                <LabelsInputContainer>
                  <ValidationTextField
                    control={control}
                    rules={addNewBatchModalRules.secondLabel}
                    name="secondLabel"
                    label="Scan or enter second label"
                  />
                </LabelsInputContainer>
              </LabelsContainer>
            </HalfScreen>
            <HalfScreen>
              <SectionTitle variant="BodyMediumBold" color={Colors.Text.Paragraph}>
                Last Labels
              </SectionTitle>
              <LabelsContainer>
                <LabelsInputContainer>
                  <ValidationTextField
                    control={control}
                    name="lastLabel"
                    rules={addNewBatchModalRules.lastLabel}
                    label="Scan or enter last label"
                  />
                </LabelsInputContainer>
                <LabelsInputContainer>
                  <ValidationTextField
                    control={control}
                    name="secondLastLabel"
                    rules={addNewBatchModalRules.secondLastLabel}
                    label="Scan or enter second last label"
                  />
                </LabelsInputContainer>
              </LabelsContainer>
            </HalfScreen>
          </Row>
          <Row>
            <HalfScreen isLeftHalf>
              <SectionTitle variant="BodyMediumBold" color={Colors.Text.Paragraph}>
                LOT number
              </SectionTitle>
              <LabelsInputContainer>
                <ValidationTextField
                  control={control}
                  name="lotNumber"
                  label="Enter LOT number"
                  rules={addNewBatchModalRules.lotNumber}
                />
              </LabelsInputContainer>
            </HalfScreen>
            <HalfScreen>
              <SectionTitle variant="BodyMediumBold" color={Colors.Text.Paragraph}>
                Product
              </SectionTitle>
              <ModalDropdownInput
                name={'product'}
                fullWidth
                rules={addNewBatchModalRules.product}
                control={control}
                dropdownItems={products}
                selectedItem={watch('product')}
                onSelect={() => {}}
              />
            </HalfScreen>
          </Row>

          <Row>
            <HalfScreen isLeftHalf>
              <SectionTitle variant="BodyMediumBold" color={Colors.Text.Paragraph}>
                Expiry date
              </SectionTitle>
              <Controller
                name="expiryDate"
                control={control}
                rules={addNewBatchModalRules.expiryDate}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <DatePicker
                    selected={value ? new Date(value) : undefined}
                    minDate={new Date()}
                    onChange={(date: Date) => {
                      onChange(date.toString());
                    }}
                    customInput={renderDateInput({ value, error: error?.message })}
                  />
                )}
              />
            </HalfScreen>
            <HalfScreen>
              <SectionTitle variant="BodyMediumBold" color={Colors.Text.Paragraph}>
                Prediction model
              </SectionTitle>
              <ModalDropdownInput
                name={'predictionModel'}
                rules={addNewBatchModalRules.predictionModel}
                fullWidth
                selectedItem={watch('predictionModel')}
                control={control}
                dropdownItems={pipelineModels?.map((model) => model.name)}
                onSelect={() => {}}
              />
            </HalfScreen>
          </Row>

          <ActionButton variant="contained" onClick={handleSubmit(onSubmit)}>
            Continue
          </ActionButton>
        </ContentContainer>
      ) : (
        <ContentContainer>
          <TitleContainer>
            <Title variant="Heading3" color={Colors.Text.Heading}>
              Batch Summary
            </Title>
            <Typography variant="BodyMediumRegular" color={Colors.Text.Paragraph}>
              Review the batch details before submission.
            </Typography>
          </TitleContainer>
          <Table stickyHeader>
            <TableHead>
              <HeaderTableRow>
                <HeaderTableCell>Field</HeaderTableCell>
                <HeaderTableCell>Value</HeaderTableCell>
              </HeaderTableRow>
            </TableHead>
            <TableBody data-testid="consumersTableBody">
              {Object.entries(getValues()).map(([key, value]) => (
                <TableRow key={key}>
                  <BodyTableCell>{camelCaseToTitleCase(key)}</BodyTableCell>
                  <BodyTableCell>{key === 'expiryDate' ? formatDate(value) : value}</BodyTableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <ActionButton variant="outlined" onClick={onEdit}>
            Edit Details
          </ActionButton>
          <ActionButton variant="contained" onClick={onActivateRange} disabled={creatingBatch}>
            Submit Batch
          </ActionButton>
        </ContentContainer>
      )}
      <Toast
        variant={toastConfig.variant}
        title={toastConfig.title}
        isOpen={toastConfig.isOpen}
        handleClose={closeToast}
      />
      <Dialog onClose={closeErrorDialog} open={!!errorDialogContent} fullWidth>
        <DialogTitle>{errorDialogContent?.title}</DialogTitle>
        <DialogContent>
          <Typography variant="BodyMediumRegular" color={Colors.Text.Paragraph}>
            {errorDialogContent?.description}
          </Typography>
        </DialogContent>
        <DialogActions>
          <ActionButton size="medium" onClick={closeErrorDialog}>
            Continue
          </ActionButton>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
}

export default ManufacturingPage;
